import type { Reducer, StateFromReducersMapObject } from 'redux';
import { combineReducers } from 'redux';

import { USER_LOGOUT_CLEAR } from 'constants/ActionTypes';
import appReducer from 'modules/app/reducer';
import appointments from 'modules/appointments/reducer';
import appointmentTask from 'modules/appointmentTask/reducer';
import newAuditModels from 'modules/auditModels/reducer';
import audit from 'modules/audits/reducer';
import auditStatistics from 'modules/auditStatistics/reducer';
import { SIGN_OUT_EMERGENCY } from 'modules/auth/actionTypes';
import auth from 'modules/auth/reducer';
import bodyScan from 'modules/bodyScan/reducers';
import brandingTemplates from 'modules/brandingTemplates/reducer';
import brands from 'modules/brands/reducers';
import cameras from 'modules/cameras/reducer';
import campaignFiles from 'modules/campaignFiles/reducers';
import campaigns from 'modules/campaigns/reducers';
import ccm from 'modules/ccm/reducers';
import chat from 'modules/chat/reducer';
import checklists from 'modules/checklists/reducers';
import config from 'modules/config/reducer';
import configurator from 'modules/configurator/reducers';
import contactLists from 'modules/contactLists/reducers';
import contactMembers from 'modules/contactMembers/reducers';
import controlList from 'modules/controlList/reducer';
import countries from 'modules/countries/reducer';
import dealerConfiguration from 'modules/dealerConfiguration/reducers';
import dealers from 'modules/dealers/reducer';
import devices from 'modules/devices/reducer';
import events from 'modules/events/reducer';
import followUp from 'modules/followUp/reducers';
import gates from 'modules/gates/reducers';
import type { Action } from 'modules/global/generic.types';
import newGroups from 'modules/groups/reducers';
import groupTemplates from 'modules/groupTemplates/reducer';
import guidelines from 'modules/guidelines/reducers';
import hardwares from 'modules/hardwares/reducers';
import i18n from 'modules/i18n/reducer';
import interventionReducer from 'modules/interventions/reducers';
import invoices from 'modules/invoices/reducer';
import kiosks from 'modules/kiosks/reducer';
import leads from 'modules/leads/reducer';
import lockers from 'modules/lockers/reducer';
import mediaLibrary from 'modules/mediaLibrary/reducer';
import medias from 'modules/medias/reducer';
import messagingAccounts from 'modules/messagingAccounts/reducers';
import metrics from 'modules/metrics/reducers';
import notifications from 'modules/notifications/reducer';
import payers from 'modules/payers/reducer';
import payments from 'modules/payments/reducer';
import predefinedMessages from 'modules/predefinedMessages/reducer';
import publicToken from 'modules/publicToken/reducer';
import questionModels from 'modules/questionModels/reducer';
import releases from 'modules/releases/reducers';
import reportIssues from 'modules/reportIssues/reducers';
import type { RouterState } from 'modules/router/types';
import screensaver from 'modules/screensaver/reducers';
import selfServices from 'modules/selfServices/reducer';
import serviceHeaders from 'modules/serviceHeaders/reducer';
import dealerStatistics from 'modules/statistics/reducer';
import steps from 'modules/steps/reducer';
import tags from 'modules/tags/reducer';
import templates from 'modules/templates/reducer';
import tireBrands from 'modules/tireBrands/reducers';
import tireQuote from 'modules/tireQuote/reducers';
import tireScan from 'modules/tireScan/reducers';
import toastNotifications from 'modules/toast-notifications/reducer';
import userGroups from 'modules/userGroups/reducer';
import newUsers from 'modules/users/reducer';
import vehicleCheckModels from 'modules/vehicleCheckModels/reducers';
import whitelist from 'modules/whitelist/reducer';

import categoriesStatistics from './categoriesStatistics';
import customers from './customersReducer';
import dealerConfig from './dealerConfigReducer';
import derivatives from './derivativeReducer';
import extensions from './extensionReducer';
import languagesReducer from './languageReducer';
import models from './modelReducer';
import newOffer from './newOfferReducer';
import offers from './offerReducer';
import pack from './packReducer';
import tva from './tvaReducer';
import users from './userReducer';
import variants from './variantReducer';

const reducer = {
  app: appReducer,
  i18n,
  appointments,
  appointmentTask,
  newAuditModels,
  audit,
  auditStatistics,
  auth,
  brandingTemplates,
  brands,
  bodyScan,
  campaigns,
  campaignFiles,
  cameras,
  hardwares,
  checklists,
  config,
  configurator,
  contactLists,
  contactMembers,
  countries,
  customers,
  dealerConfig,
  dealers,
  dealerStatistics,
  categoriesStatistics,
  ccm,
  chat,
  controlList,
  dealerConfiguration,
  derivatives,
  devices,
  events,
  extensions,
  followUp,
  gates,
  guidelines,
  reportIssues,
  groupTemplates,
  invoices,
  kiosks,
  languages: languagesReducer,
  leads,
  lockers,
  medias,
  mediaLibrary,
  messagingAccounts,
  metrics,
  models,
  newGroups,
  interventions: interventionReducer,
  newOffer,
  newUsers,
  notifications,
  offers,
  pack,
  payers,
  payments,
  predefinedMessages,
  publicToken,
  questionModels,
  releases,
  screensaver,
  selfServices,
  serviceHeaders,
  steps,
  tags,
  templates,
  tireBrands,
  tireQuote,
  tireScan,
  toastNotifications,
  tva,
  users,
  userGroups,
  variants,
  vehicleCheckModels,
  whitelist,
};

const fleetbackAppReducer = (router: Reducer<RouterState>) =>
  combineReducers({
    router,
    ...reducer
  });

// Hook to clear data when we logout
const fleetbackReducer = (routerReducer: Reducer<RouterState>) => (state: RootState | undefined, action: Action) => {
  if (state && (action.type === USER_LOGOUT_CLEAR || action.type === SIGN_OUT_EMERGENCY)) {
    const { languages, i18n: translations, app, router, auth: authReducer } = state;

    return fleetbackAppReducer(routerReducer)(
      { router, languages, i18n: translations, app, auth: authReducer },
      action
    );
  }

  return fleetbackAppReducer(routerReducer)(state, action);
};

export type RootState = { router: RouterState } & StateFromReducersMapObject<typeof reducer>;
export default fleetbackReducer;
